import React from 'react';
import { useNavigate } from 'react-router-dom';

interface NavigableHtmlProps {
    html: string;
    style?: React.CSSProperties;
}

/**
 * Renders an HTML string and intercepts clicks on relative anchor tags,
 * routing them through React Router to preserve SPA behavior.
 * Uses a native DOM event listener (not React synthetic events) to reliably
 * intercept clicks on elements created via dangerouslySetInnerHTML.
 * External links (different origin) fall through to normal browser navigation.
 */
const NavigableHtml: React.FC<NavigableHtmlProps> = ({ html, style }) => {
    const navigate = useNavigate();
    const containerRef = React.useRef<HTMLSpanElement>(null);

    React.useEffect(() => {
        const element = containerRef.current;
        if (!element) return undefined;

        const handleClick = (e: MouseEvent): void => {
            const anchor = (e.target as Element).closest('a');
            if (!anchor) return;

            // Let target="_blank" and download links fall through to the browser
            if (anchor.target === '_blank' || anchor.hasAttribute('download')) return;

            const href = anchor.getAttribute('href');
            if (!href) return;

            try {
                const url = new URL(href, window.location.origin);
                if (url.origin === window.location.origin) {
                    e.preventDefault();
                    navigate(url.pathname + url.search);
                }
            } catch {
                // Malformed URL — let the browser handle it
            }
        };

        element.addEventListener('click', handleClick);
        return (): void => { element.removeEventListener('click', handleClick); };
    }, [navigate]);

    return <span ref={containerRef} dangerouslySetInnerHTML={{ __html: html }} style={style} />;
};

export default NavigableHtml;
